import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { roleSchema, type Role } from '@lms/shared-types'
import { setViewAsEmployeeId } from '../../api/client'

/**
 * "Viewing As" — read-only impersonation for HR / Director / Exec Director.
 *
 * The *real* role (from `useSession`) still drives access control and is what
 * the server sees on every request. This context lets a privileged user pick
 * an employee to *view* the app as — sidebar, page filters and balance
 * widgets render against the impersonated employee's identity, but mutations
 * still travel under the real session.
 *
 * Persisted to sessionStorage so a tab reload doesn't drop the selection.
 */

export interface ImpersonatedEmployee {
  id: string
  name: string
  preferredName: string | null
  email: string
  role: Role
  teamId: string
  teamName: string | null
}

export interface EffectiveEmployeeState {
  /** When non-null, the user is viewing-as. */
  impersonated: ImpersonatedEmployee | null
  setImpersonated: (next: ImpersonatedEmployee | null) => void
  clear: () => void
}

const EffectiveEmployeeContext = createContext<EffectiveEmployeeState | null>(null)

const STORAGE_KEY = 'lms.viewing-as.v1'

function readStored(): ImpersonatedEmployee | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    const obj = parsed as Record<string, unknown>
    const role = roleSchema.safeParse(obj.role)
    if (!role.success) return null
    if (
      typeof obj.id !== 'string' ||
      typeof obj.name !== 'string' ||
      typeof obj.email !== 'string' ||
      typeof obj.teamId !== 'string'
    ) {
      return null
    }
    return {
      id: obj.id,
      name: obj.name,
      preferredName: typeof obj.preferredName === 'string' ? obj.preferredName : null,
      email: obj.email,
      role: role.data,
      teamId: obj.teamId,
      teamName: typeof obj.teamName === 'string' ? obj.teamName : null,
    }
  } catch {
    return null
  }
}

function writeStored(value: ImpersonatedEmployee | null): void {
  if (typeof window === 'undefined') return
  try {
    if (value == null) {
      window.sessionStorage.removeItem(STORAGE_KEY)
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    }
  } catch {
    // sessionStorage may throw in privacy mode — silently degrade.
  }
}

interface EffectiveEmployeeProviderProps {
  children: ReactNode
}

export function EffectiveEmployeeProvider({
  children,
}: EffectiveEmployeeProviderProps): JSX.Element {
  const [impersonated, setImpersonatedState] = useState<ImpersonatedEmployee | null>(
    () => readStored(),
  )

  const setImpersonated = useCallback((next: ImpersonatedEmployee | null) => {
    setImpersonatedState(next)
    writeStored(next)
  }, [])

  const clear = useCallback(() => {
    setImpersonatedState(null)
    writeStored(null)
  }, [])

  // Sync the impersonated id into the API client so every request
  // transparently carries `X-View-As: <id>` while viewing-as is active,
  // and clears it when the user exits (clear() sets impersonated → null).
  //
  // On an actual identity change we ALSO drop the query cache: role-scoped
  // pages (e.g. Division Overview) use static query keys, so without this
  // TanStack Query would serve the prior identity's cached result and the
  // new `X-View-As` header would never trigger a refetch. Skip the initial
  // mount (prevId === undefined) so a normal page load doesn't refetch twice.
  const queryClient = useQueryClient()
  const prevViewAsIdRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const id = impersonated?.id ?? null
    setViewAsEmployeeId(id)
    if (prevViewAsIdRef.current !== undefined && prevViewAsIdRef.current !== id) {
      void queryClient.invalidateQueries()
    }
    prevViewAsIdRef.current = id
  }, [impersonated, queryClient])

  // If something else (another tab, dev tools) clears storage, sync.
  useEffect(() => {
    if (typeof window === 'undefined') return
    function onStorage(event: StorageEvent): void {
      if (event.key !== STORAGE_KEY) return
      setImpersonatedState(readStored())
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const value = useMemo<EffectiveEmployeeState>(
    () => ({ impersonated, setImpersonated, clear }),
    [impersonated, setImpersonated, clear],
  )

  return (
    <EffectiveEmployeeContext.Provider value={value}>
      {children}
    </EffectiveEmployeeContext.Provider>
  )
}

export function useEffectiveEmployeeState(): EffectiveEmployeeState {
  const ctx = useContext(EffectiveEmployeeContext)
  if (!ctx) {
    throw new Error(
      'useEffectiveEmployeeState must be used inside <EffectiveEmployeeProvider>',
    )
  }
  return ctx
}

/**
 * Returns the *effective* employee id pages should query against:
 *   - the impersonated employee's id when "Viewing As" is active,
 *   - otherwise the real signed-in user's id (passed in by the caller, since
 *     this hook deliberately doesn't import the auth context to keep the
 *     dependency arrow shell → auth, not auth → shell).
 *
 * Pages call: `useEffectiveEmployeeId(session.user.id)`.
 */
export function useEffectiveEmployeeId(realEmployeeId: string | null): string | null {
  const { impersonated } = useEffectiveEmployeeState()
  return impersonated?.id ?? realEmployeeId
}

/**
 * Returns the *effective* role pages and shell chrome should render for:
 *   - the impersonated employee's role when active,
 *   - otherwise the real role.
 */
export function useEffectiveRole(realRole: Role): Role {
  const { impersonated } = useEffectiveEmployeeState()
  return impersonated?.role ?? realRole
}
